import React from 'react';
import styled, { useTheme } from 'styled-components';
import toast from 'react-hot-toast';

import { useGlobalModalContext } from '../../context/GlobalModal';
import { Button, Input, Modal, SelectForm } from '../../shared/Components';
import { breakpoints } from '../../theme';
import { FlexContainer, Image, Text, Title } from '../../shared/Typhography';
import { ApprovePuzzleField, approvePuzzleSchema } from '../../validations';
import { useForm } from '../../hooks';
import { getAllCollections, updatePuzzle } from '../../services';
import { PuzzleStatus } from '../../types';
import { strings } from '../../strings';
import { SubmitImageIcon } from '../../assets/Modal';

const Container = styled.div`
  display: grid;
  width: 100%;
  height: 100%;
  gap: 2rem;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-template-rows: 50px 50px 1fr 1fr;
  gap: 0px 0px;
  grid-template-areas:
    'title title . .'
    'slika slika inputs inputs'
    'slika slika inputs inputs'
    'slika slika buttons buttons';
  @media (${breakpoints.lg}) {
    gap: 1rem;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr 1fr;
    grid-template-areas:
      'title title'
      'slika slika'
      'inputs inputs'
      'buttons buttons';
  }
`;

const Img = styled(Image)`
  grid-area: slika;
  justify-self: center;
  align-self: center;
`;

const Inputs = styled.div`
  grid-area: inputs;
  display: flex;
  flex-direction: column;
  gap: 2em;
  width: 80%;
  justify-self: center;
  align-self: center;
`;

const Buttons = styled.div`
  grid-area: buttons;
  justify-self: center;
  align-self: center;
  display: flex;
  gap: 3rem;
`;

const Heading = styled(Title)`
  grid-area: title;
`;

type Collection = {
  value: string;
  id: number;
  label: string;
};

interface ApprovePuzzleInputs {
  [ApprovePuzzleField.KEYWORDS]: string;
  [ApprovePuzzleField.COLLECTION]: Collection;
  [ApprovePuzzleField.CALCULATED_ROYALTY]: boolean;
}

const ApprovePuzzleModal = () => {
  const { colors } = useTheme();
  const { hideModal, store } = useGlobalModalContext();
  const { modalProps } = store || {};
  const { title, puzzle } = modalProps || {};
  const { data: collectionsData, isLoading } = getAllCollections();
  const [collection, setCollection] = React.useState<Collection[] | undefined>(undefined);
  const { mutate: update } = updatePuzzle(puzzle.id);
  const [confirmModalOpened, setConfirmModalOpened] = React.useState<boolean>(false);
  const {
    handleSubmit,
    setValue,
    control,
    trigger,
    reset,
    formState: { errors },
  } = useForm<ApprovePuzzleInputs>({
    schema: approvePuzzleSchema,
  });

  const closeModal = () => {
    hideModal();
    reset();
  };

  const getFinalData = () => {
    const finalData = collectionsData?.docs.map((item) => ({
      value: item.id,
      label: item.data().name,
      id: item.data().id,
    }));
    setCollection(finalData);
  };

  const submit = handleSubmit((data) => {
    try {
      update({
        keywords: data.keywords,
        collectionIdFirebase: data.collectionId.value,
        collectionId: data.collectionId.id,
        status: PuzzleStatus.APPROVED,
        locked: false,
        calculatedRoyalty: data.calculatedRoyalty,
        likes: 0,
        plays: 0,
        Revenue: 0,
      });
    } catch (error) {
      toast.error(strings.toastNotificationSomethingWrong);
    }
    toast.success(strings.successfullyApprovedPuzzleNotification);
    reset();
    closeModal();
  });

  const openModal = async () => {
    const result = await trigger();
    if (!result) {
      return;
    }
    setConfirmModalOpened(true);
  };

  const closeConfirm = () => {
    setConfirmModalOpened(false);
  };

  React.useEffect(() => {
    if (!isLoading) {
      getFinalData();
      setValue(ApprovePuzzleField.KEYWORDS, puzzle.keywords);
    }
  }, [isLoading]);

  return (
    <Modal padding="30px" width={1000} closeModal={closeModal} onRequestClose={closeModal}>
      <Container>
        <Heading as="h3">{title}</Heading>
        <Img
          size="largeImageScreen"
          borderRadiusType="rounded"
          src={puzzle.image}
          alt={puzzle.title}
        />
        <Inputs>
          <Input
            required
            name={ApprovePuzzleField.KEYWORDS}
            placeholder={ApprovePuzzleField.KEYWORDS}
            control={control}
            error={errors[ApprovePuzzleField.KEYWORDS]}
          />
          <SelectForm
            required
            placeholder={strings.selectCollection}
            name={ApprovePuzzleField.COLLECTION}
            control={control}
            values={collection}
            error={errors[ApprovePuzzleField.COLLECTION]}
          />
          <FlexContainer flexDirection="column">
            <Text padding="0 0 10px 0" weight="bold" color={colors.gray}>
              Set Calculated Royalty:
            </Text>
            <Input
              type="checkbox"
              required
              width="2rem"
              height="2rem"
              color={colors.green}
              defaultValue={true}
              name={ApprovePuzzleField.CALCULATED_ROYALTY}
              placeholder={ApprovePuzzleField.CALCULATED_ROYALTY}
              control={control}
              error={errors[ApprovePuzzleField.CALCULATED_ROYALTY]}
            />
          </FlexContainer>
        </Inputs>
        <Buttons>
          <Button variant="link" onClick={closeModal}>
            {strings.cancel}
          </Button>
          <Button variant="success" height={40} width={140} onClick={openModal}>
            {strings.approve}
          </Button>
        </Buttons>
        <Modal
          width={350}
          height={350}
          isOpen={confirmModalOpened}
          onRequestClose={closeConfirm}
          closeModal={closeConfirm}
        >
          <FlexContainer
            height="100%"
            justifyContent="space-between"
            alignItems="center"
            flexDirection="column"
          >
            <img width={100} height={100} src={SubmitImageIcon} alt="Submit Image" />
            <h1>{strings.submitImage}?</h1>
            <Text size={0.9} color={colors.gray}>
              {strings.areYouSureApprovePuzzle}
            </Text>
            <FlexContainer justifyContent="center" gap="1rem" flexDirection="row">
              <Button variant="link" onClick={closeConfirm}>
                {strings.cancel}
              </Button>
              <Button variant="success" height={40} width={140} onClick={submit}>
                {strings.submit}
              </Button>
            </FlexContainer>
          </FlexContainer>
        </Modal>
      </Container>
    </Modal>
  );
};

export default ApprovePuzzleModal;
